import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { AnalyticsService } from './core/services/analytics.service';
import { AuthService } from './core/services/auth.service';
import { normalizeLanguage } from './core/i18n/language';
import { I18nService } from './core/services/i18n.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.scss',
})
export class App {
  constructor() {
    this.followUrlLanguage();
    this.startAnalytics();
  }

  /**
   * Honours `?lang=fi|en` (or `?language=`) on entry and on every later navigation — campaign/ERP
   * links, an English customer following a shared URL, or a link inside the funnel that sets it.
   *
   * The entry value is read from `window.location` rather than the router so the language is set
   * before the first child renders. After that, a hint is applied only when it *changes*: in-funnel
   * links merge query params, so a `lang=en` from the entry URL rides along on every step, and
   * re-applying it would undo a customer's later pick from the switcher. Absent/unrecognized values
   * fall through to the persisted default the I18nService already resolved.
   */
  private followUrlLanguage(): void {
    const i18n = inject(I18nService);
    const router = inject(Router);
    // Normalized, so `lang=EN` after `lang=en` (or `language=en`) counts as unchanged.
    const hintOf = (params: { get(name: string): string | null }) =>
      normalizeLanguage(params.get('lang') ?? params.get('language'));

    let lastHint = hintOf(new URLSearchParams(window.location.search));
    i18n.applyUrlLanguage(lastHint);

    router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const hint = hintOf(router.parseUrl(event.urlAfterRedirects).queryParamMap);
        if (hint === lastHint) return;
        lastHint = hint;
        i18n.applyUrlLanguage(hint);
      });
  }

  /**
   * Boots Google Tag Manager.
   *
   * Immediately, and not after the webshop session request: page views are sent from the router's
   * navigations, and the first of those happens within milliseconds of this constructor. Waiting
   * would cost the entry page view of every visit — the one that carries the campaign and referrer
   * attribution — to learn a flag that only labels the hits.
   *
   * So the session is applied when it arrives, a moment later. The cost is that the very first page
   * view of a staff session can go out without `loggedInToErp`; every later one carries it, as does
   * every purchase, which waits for the session on the confirmation page precisely because its
   * `affiliation` must be right.
   */
  private startAnalytics(): void {
    const analytics = inject(AnalyticsService);
    const auth = inject(AuthService);
    const queryClient = inject(QueryClient);

    analytics.init(false);

    queryClient
      .ensureQueryData(auth.webshopSessionOptions())
      .then((session) => analytics.init(session.isInternalUser))
      .catch(() => {
        // No session, no label. The container is already up and measuring.
      });
  }
}
